import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./style.css";
import { useNavigate } from "react-router-dom";
import { isLoggedIn, getCurrentShopId, getAuthToken } from "./auth";
import Papa from "papaparse";
import { Html5Qrcode } from "html5-qrcode";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

console.log("PRODUCT PAGE API URL:", API_BASE_URL);

const normalizeContainerSizeMl = (value) => {
  if (
    value === undefined ||
    value === null ||
    String(value).trim() === ""
  ) {
    return null;
  }

  const cleaned = String(value)
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/,/g, ".");

  // Examples: 330, 330ML
  const mlMatch = cleaned.match(/^(\d+(?:\.\d+)?)ML$/);

  if (mlMatch) {
    const millilitres = Number(mlMatch[1]);

    return Number.isFinite(millilitres)
      ? Math.round(millilitres)
      : null;
  }

  // Examples: 1L, 1LTR, 1.25L, 1.5LTR
  const litreMatch = cleaned.match(
    /^(\d+(?:\.\d+)?)(L|LTR|LITRE|LITRES)$/
  );

  if (litreMatch) {
    const litres = Number(litreMatch[1]);

    return Number.isFinite(litres)
      ? Math.round(litres * 1000)
      : null;
  }

  // Plain numeric value is treated as millilitres
  if (/^\d+(?:\.\d+)?$/.test(cleaned)) {
    const millilitres = Number(cleaned);

    return Number.isFinite(millilitres)
      ? Math.round(millilitres)
      : null;
  }

  return null;
};

const extractContainerSizeFromText = (text) => {
  const value = String(text || "").toLowerCase();

  const mlMatch = value.match(/(\d+(?:\.\d+)?)\s*ml\b/);
  if (mlMatch) {
    return normalizeContainerSizeMl(mlMatch[1]);
  }

  const litreMatch = value.match(/(\d+(?:\.\d+)?)\s*(l|ltr|litre|liter)\b/);
  if (litreMatch) {
    return normalizeContainerSizeMl(Number(litreMatch[1]) * 1000);
  }

  return null;
};

const getDrsDetailsFromSize = (containerSizeMl) => {
  const ml = normalizeContainerSizeMl(containerSizeMl);

  if (!ml) {
    return {
      is_drs: false,
      drs_amount: 0,
    };
  }

  if (ml >= 150 && ml <= 500) {
    return {
      is_drs: true,
      drs_amount: 0.15,
    };
  }

  if (ml > 500 && ml <= 3000) {
    return {
      is_drs: true,
      drs_amount: 0.25,
    };
  }

  return {
    is_drs: false,
    drs_amount: 0,
  };
};

const getCostPrice = (product) => {
  return Number(
    product.cost_price ||
      product.costPrice ||
      product.cost ||
      product.buying_price ||
      product.buyingPrice ||
      0,
  );
};

const buildProductPayload = (product, shopId, options = {}) => {
  const inferSizeFromName = options.inferSizeFromName !== false;

  const manualSize = normalizeContainerSizeMl(
    product.container_size_ml
  );

  const inferredSize = inferSizeFromName
    ? extractContainerSizeFromText(product.name)
    : null;

  const finalContainerSizeMl =
    manualSize || inferredSize || null;

  const drs = getDrsDetailsFromSize(
    finalContainerSizeMl
  );

  return {
    ...product,

    shop_id: shopId,

    price: Number(product.price || 0),

    cost_price: getCostPrice(product),

    stock: parseInt(product.stock || 0, 10),

    category: product.category || "grocery",

    vat_code: product.vat_code || "B",

    supplier_id:
      product.supplier_id !== undefined &&
      product.supplier_id !== null &&
      String(product.supplier_id).trim() !== ""
        ? Number(product.supplier_id)
        : null,

    container_size_ml: finalContainerSizeMl,

    is_drs: drs.is_drs,

    drs_amount: drs.drs_amount,
  };
};

function ProductPage() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [editingProduct, setEditingProduct] = useState(null);
  const [importProducts, setImportProducts] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [shopSettings, setShopSettings] = useState(null);
  const [shopSettingsLoaded, setShopSettingsLoaded] = useState(false);
  const [shopId, setShopId] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  const [cameraScannerOpen, setCameraScannerOpen] = useState(false);
  const [cameraScannerError, setCameraScannerError] = useState("");
  const scannerRef = useRef(null);
  const scannerTargetRef = useRef("add");

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");

  const barcodeInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const csvFileInputRef = useRef(null);

const [form, setForm] = useState({
  name: "",
  price: "",
  cost_price: "",
  barcode: "",
  stock: "",
  category: "",
  vat_code: "B",
  container_size_ml: "",
  supplier_id: "",
});

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/admin-login");
      return;
    }

    const currentShopId = getCurrentShopId();

    console.log("ProductPage current admin shop id:", currentShopId);

    setShopId(currentShopId);
  }, [navigate]);

  useEffect(() => {
    barcodeInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!shopId) return;

    fetchProducts();
    fetchShopSettings();
    fetchSuppliers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  // Fetch Suppliers Function

  const fetchSuppliers = async () => {
    if (!shopId) return;

    setSuppliersLoading(true);

    try {
      const token = getAuthToken();

      const res = await axios.get(`${API_BASE_URL}/suppliers`, {
        params: {
          shop_id: shopId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const supplierData = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.suppliers)
          ? res.data.suppliers
          : [];

      const activeSuppliers = supplierData.filter(
        (supplier) =>
          supplier.active === true ||
          supplier.active === 1 ||
          supplier.active === "true",
      );

      setSuppliers(activeSuppliers);
    } catch (err) {
      console.error(
        "Fetch suppliers error:",
        err.response?.data || err.message,
      );

      setSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  };

  // Fetch Products

  const fetchProducts = async () => {
    if (!shopId) return;

    try {
      const res = await axios.get(`${API_BASE_URL}/products`, {
        params: { shop_id: shopId },
      });

      console.log("PRODUCTS FOR SHOP:", shopId);
      console.log("PRODUCT DATA:", res.data);

      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.log("FETCH PRODUCTS ERROR:", err.response?.data || err.message);
    }
  };

const fetchShopSettings = async () => {
  if (!shopId) return;

  setShopSettingsLoaded(false);

  try {
    const token = getAuthToken();

    const res = await axios.get(`${API_BASE_URL}/shop-settings`, {
      params: { shop_id: shopId },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log("PRODUCT PAGE SHOP SETTINGS:", res.data);

    setShopSettings(res.data || {});
  } catch (err) {
    console.error(
      "Fetch shop settings error:",
      err.response?.data || err.message,
    );

    setShopSettings(null);
  } finally {
    setShopSettingsLoaded(true);
  }
};

  const groceryCategories = [
  "grocery",
  "softdrink",
  "crisps",
  "vape",
  "news-magz",
  "ice-cream",
  "confectionery",
  "bakery",
  "tobacco",
  "lottery",
  "payzone",
];

  const vapeCategories = [
    "vape",
    "e-liquid",
    "disposable vape",
    "pods",
    "coils",
    "tanks",
    "accessories",
    "confectionery",
    "tobacco",
    "device",
    "parts",
    "lcds",
    "battery",
    "new device",
  ];

const currentShopType = String(
  shopSettings?.shop_type ||
    shopSettings?.shopType ||
    shopSettings?.shop_table_type ||
    "",
)
  .trim()
  .toLowerCase();

console.log("PRODUCT PAGE CURRENT SHOP TYPE:", currentShopType);

let categories = [];

if (shopSettingsLoaded) {
  if (currentShopType === "vape") {
    categories = vapeCategories;
  } else if (
    currentShopType === "grocery" ||
    currentShopType === "off_licence" ||
    currentShopType === "takeaway"
  ) {
    categories = groceryCategories;
  }
}

useEffect(() => {
  if (!shopSettingsLoaded || !currentShopType) {
    return;
  }

  setForm((prev) => {
    const validCategories =
      currentShopType === "vape"
        ? vapeCategories
        : groceryCategories;

    // Keep current category if it is valid for this shop
    if (
      prev.category &&
      validCategories.includes(prev.category)
    ) {
      return prev;
    }

    return {
      ...prev,
      category:
        currentShopType === "vape"
          ? "vape"
          : "grocery",
    };
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [shopSettingsLoaded, currentShopType]);

  const handleEdit = (product) => {
    const currentSize =
      product.container_size_ml !== null &&
      product.container_size_ml !== undefined
        ? product.container_size_ml
        : extractContainerSizeFromText(product.name) || "";

    setEditingProduct({
      ...product,
      cost_price: product.cost_price || product.costPrice || "",
      container_size_ml: currentSize,
      supplier_id:
        product.supplier_id !== null && product.supplier_id !== undefined
          ? String(product.supplier_id)
          : "",
    });

    setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
  };

  const getCsvValue = (row, possibleNames) => {
    const matchingKey = Object.keys(row).find((key) =>
      possibleNames.includes(String(key).trim().toLowerCase()),
    );

    if (!matchingKey) {
      return "";
    }

    return row[matchingKey];
  };


  const clearCsvImport = () => {
  setImportProducts([]);
  setImportErrors([]);
  setImportFileName("");
  setImportMessage("");
  setIsImporting(false);

  if (csvFileInputRef.current) {
    csvFileInputRef.current.value = "";
  }
};

  const handleCsvFile = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setImportFileName(file.name);
    setImportMessage("");
    setImportErrors([]);
    setImportProducts([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => String(header).trim(),

      complete: (result) => {
        const parsedProducts = [];
        const validationErrors = [];

        result.data.forEach((row, index) => {
          const name = String(
            getCsvValue(row, [
              "name",
              "product_name",
              "product name",
              "product",
              "description",
              "item description",
              "stock description",
            ]) || "",
          ).trim();

          let barcode = String(
            getCsvValue(row, [
              "barcode",
              "ean",
              "ean13",
              "ean 13",
              "product barcode",
              "plu",
              "code",
              "stock code",
            ]) || "",
          ).trim();

          barcode = barcode.replace(/\s+/g, "");

          // Ignore completely blank CSV rows
          if (!name && !barcode) {
          return;
          }

          const priceText = String(
            getCsvValue(row, [
              "price",
              "selling_price",
              "selling price",
              "retail price",
              "sale price",
              "unit price",
              "rsp",
              "rrp",
            ]) || "",
          )
            .replace("€", "")
            .replace("£", "")
            .replace(/,/g, "")
            .trim();

          const stockText = String(
            getCsvValue(row, [
              "stock",
              "opening_stock",
              "opening stock",
              "stock quantity",
              "quantity",
              "qty",
              "current stock",
              "stock level",
            ]) || "0",
          )
            .replace(/,/g, "")
            .trim();

          const category = String(
            getCsvValue(row, [
              "category",
              "department",
              "group",
              "product group",
            ]) || "Uncategorized",
          ).trim();

          const supplierName = String(
            getCsvValue(row, ["supplier", "supplier_name", "supplier name"]) ||
              "",
          ).trim();

          let vatCode = String(
            getCsvValue(row, [
              "vat_code",
              "vat code",
              "vat",
              "tax code",
              "tax",
            ]) || "A",
          )
            .trim()
            .toUpperCase();

          const containerSizeText = String(
            getCsvValue(row, [
              "container_size_ml",
              "container size ml",
              "container size",
              "size ml",
              "size",
              "ml",
              "volume",
              "volume ml",
              "pack size",
            ]) || "",
          ).trim();

          const price = Number(priceText);
          const stock = Number(stockText || 0);

          if (!name) {
            validationErrors.push({
              row: index + 2,
              message: "Missing product name",
            });
            return;
          }

          if (!barcode) {
            validationErrors.push({
              row: index + 2,
              message: "Missing barcode",
            });
            return;
          }

          if (!Number.isFinite(price) || price < 0) {
            validationErrors.push({
              row: index + 2,
              message: "Invalid price",
            });
            return;
          }

          if (!Number.isFinite(stock) || stock < 0) {
            validationErrors.push({
              row: index + 2,
              message: "Invalid stock",
            });
            return;
          }

          if (!vatCode) {
            vatCode = "A";
          }

          const manualContainerSize =
            normalizeContainerSizeMl(containerSizeText);
          const inferredContainerSize = extractContainerSizeFromText(name);
          const finalContainerSize =
            manualContainerSize || inferredContainerSize || null;

          const drs = getDrsDetailsFromSize(finalContainerSize);

          const matchedSupplier = supplierName
            ? suppliers.find(
                (supplier) =>
                  String(supplier.name || "")
                    .trim()
                    .toLowerCase() === supplierName.toLowerCase(),
              )
            : null;

         parsedProducts.push({
           shop_id: shopId,
           name,
           barcode,
           price,
           stock,
           category,
           vat_code: vatCode,
           container_size_ml: finalContainerSize,
           is_drs: drs.is_drs,
           drs_amount: drs.drs_amount,

           supplier_id: matchedSupplier ? Number(matchedSupplier.id) : null,
         });
        });

        const seenBarcodes = new Set();
        const uniqueProducts = [];

        parsedProducts.forEach((product) => {
          if (seenBarcodes.has(product.barcode)) {
            validationErrors.push({
              barcode: product.barcode,
              message: "Duplicate barcode inside CSV file",
            });

            return;
          }

          seenBarcodes.add(product.barcode);
          uniqueProducts.push(product);
        });

        setImportProducts(uniqueProducts);
        setImportErrors(validationErrors);
        setImportMessage(`${uniqueProducts.length} valid products found`);
      },

      error: (error) => {
        console.error("CSV reading error:", error);
        setImportMessage("The CSV file could not be read");
      },
    });
  };

  const submitCsvImport = async () => {
    if (!shopId) {
      alert("Shop ID not found. Please logout and login again.");
      return;
    }

    if (importProducts.length === 0) {
      alert("There are no valid products to import");
      return;
    }

    const confirmed = window.confirm(
      `Import ${importProducts.length} products?\n\n` +
        "Existing products with the same barcode will be updated.",
    );

    if (!confirmed) {
      return;
    }

    setIsImporting(true);
    setImportMessage("");

    try {
      const response = await axios.post(`${API_BASE_URL}/products/import`, {
        shop_id: shopId,
        products: importProducts.map((product) =>
          buildProductPayload(product, shopId, { inferSizeFromName: true }),
        ),
      });

      const result = response.data;

      setImportMessage(
        `Import complete: ${result.inserted} inserted, ` +
          `${result.updated} updated, ` +
          `${result.skipped} skipped`,
      );

      alert(
        `Product import completed\n\n` +
          `Inserted: ${result.inserted}\n` +
          `Updated: ${result.updated}\n` +
          `Skipped: ${result.skipped}`,
      );

      setImportProducts([]);
      setImportErrors([]);
      setImportFileName("");

      fetchProducts();
    } catch (error) {
      console.error(
        "Product import failed:",
        error.response?.data || error.message,
      );

      setImportMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Product import failed",
      );
    } finally {
      setIsImporting(false);
    }
  };

  const handleSearch = async (searchValue = search) => {
    if (!shopId) return;

    const cleanSearch = String(searchValue || "").trim();

    try {
      if (cleanSearch === "") {
        fetchProducts();
        return;
      }

      const res = await axios.get(`${API_BASE_URL}/products/search`, {
        params: {
          shop_id: shopId,
          q: cleanSearch,
        },
      });

      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Search error:", err.response?.data || err.message);
    }
  };

  const saveProduct = async () => {
    if (!shopId) {
      alert("Shop ID not found. Please logout and login again.");
      return;
    }

    const containerSizeMl = normalizeContainerSizeMl(
      editingProduct.container_size_ml,
    );

    const drs = getDrsDetailsFromSize(containerSizeMl);

    const payload = {
      ...editingProduct,
      shop_id: shopId,
      price: parseFloat(editingProduct.price),
      stock: parseInt(editingProduct.stock || 0, 10),
      category: editingProduct.category || "grocery",
      vat_code: editingProduct.vat_code || "B",
      container_size_ml: containerSizeMl,
      is_drs: drs.is_drs,
      drs_amount: drs.drs_amount,
    };

   try {
  console.log("UPDATING PRODUCT:", payload);

  const token = getAuthToken();

  const res = await axios.put(
    `${API_BASE_URL}/products/${editingProduct.id}`,
    payload,
    {
      params: {
        shop_id: shopId,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

      console.log("UPDATE SUCCESS:", res.data);

      setProducts((prev) =>
        prev.map((p) => (p.id === editingProduct.id ? res.data : p)),
      );

      setEditingProduct(null);
      fetchProducts();
    } catch (err) {
      console.log("UPDATE PRODUCT ERROR:", err.response?.data || err.message);
      alert(err.response?.data?.error || "Product could not be updated");
    }
  };

  const deleteProduct = async (product) => {
    if (!shopId) {
      alert("Shop ID not found. Please logout and login again.");
      return;
    }

    const productId = product?.id || product;
    const productName = product?.name || "this product";
    const productBarcode = product?.barcode || "-";

    const confirmed = window.confirm(
      `Are you sure you want to delete this product?\n\n` +
        `Product: ${productName}\n` +
        `Barcode: ${productBarcode}\n\n` +
        `This action cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    console.log("Deleting product:", product);

    try {
  const token = getAuthToken();

  const res = await axios.delete(
    `${API_BASE_URL}/products/${productId}`,
    {
      params: {
        shop_id: shopId,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

      console.log("DELETE RESPONSE:", res.data);

      fetchProducts();
    } catch (err) {
      console.log("DELETE ERROR:", err.response?.data || err.message);
      alert(err.response?.data?.error || "Product could not be deleted");
    }
  };

  const validateProduct = (product) => {
    const errors = {};

    // Product Name
    if (!product.name?.trim()) {
      errors.name = "Product name is required.";
    } else if (product.name.trim().length > 100) {
      errors.name = "Product name cannot exceed 100 characters.";
    }

    // Barcode
    if (!product.barcode?.trim()) {
      errors.barcode = "Barcode is required.";
    }

    // Selling Price
    if (product.price === "" || isNaN(product.price)) {
      errors.price = "Selling price is required.";
    } else if (Number(product.price) <= 0) {
      errors.price = "Selling price must be greater than €0.";
    }

    // Cost Price
    if (
      product.cost_price !== "" &&
      (isNaN(product.cost_price) || Number(product.cost_price) < 0)
    ) {
      errors.cost_price = "Cost price cannot be negative.";
    }

    // Stock
    if (product.stock === "" || isNaN(product.stock)) {
      errors.stock = "Stock is required.";
    } else if (Number(product.stock) < 0) {
      errors.stock = "Stock cannot be negative.";
    }

    // Category
    if (!product.category) {
      errors.category = "Please select a category.";
    }

    // VAT
    if (!product.vat_code) {
      errors.vat_code = "Please select a VAT code.";
    }

    return errors;
  };

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();

    if (!shopId) {
      alert("Shop ID not found. Please logout and login again.");
      return;
    }

    const validationErrors = validateProduct(form);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSaving(true);

    const containerSizeMl = normalizeContainerSizeMl(form.container_size_ml);
    const drs = getDrsDetailsFromSize(containerSizeMl);

    const payload = {
      ...form,
      shop_id: shopId,
      price: parseFloat(form.price),
      stock: parseInt(form.stock || 0, 10),
      category: form.category || "grocery",
      vat_code: form.vat_code || "B",
      container_size_ml: containerSizeMl,
      is_drs: drs.is_drs,
      drs_amount: drs.drs_amount,
    };

    console.log("SENDING PRODUCT FOR SHOP:", shopId);
    console.log("SENDING PRODUCT PAYLOAD:", payload);

   try {
  const token = getAuthToken();

  const res = await axios.post(
    `${API_BASE_URL}/products`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

      console.log("POST SUCCESS:", res.data);

      console.log("SUCCESS MESSAGE SET");

      setSuccessMessage("Product added successfully");
      setErrors({});

      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);

      setForm((current) => ({
        ...current,
        name: "",
        price: "",
        cost_price: "",
        barcode: "",
        stock: "",
        container_size_ml: "",
        supplier_id: "",
        category: current.category || "grocery",
        vat_code: current.vat_code || "B",
      }));

      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);

      fetchProducts();
    } catch (err) {
      console.log("POST ERROR:", err.response?.data || err.message);
      alert(err.response?.data?.error || "Product could not be added");
    } finally {
      setIsSaving(false);
    }
  };

  const getDrsLabel = (product) => {
    const size = normalizeContainerSizeMl(product.container_size_ml);
    const amount = Number(product.drs_amount || 0);
    const isDrs =
      product.is_drs === true ||
      product.is_drs === 1 ||
      product.is_drs === "true" ||
      amount > 0;

    if (!size || !isDrs) {
      return "-";
    }

    return `${size}ml / €${amount.toFixed(2)}`;
  };

  // Mobile bar code functionalities

  const stopMobileCameraScanner = async () => {
    try {
      if (scannerRef.current) {
        const scannerState = scannerRef.current.getState();

        if (scannerState === 2 || scannerState === 3) {
          await scannerRef.current.stop();
        }

        await scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (error) {
      console.error("Scanner stop error:", error);
    }

    setCameraScannerOpen(false);
  };

  const handleScannedBarcode = async (barcode) => {
    const cleanBarcode = String(barcode || "").trim();

    if (!cleanBarcode) return;

    if (scannerTargetRef.current === "search") {
      setSearch(cleanBarcode);

      await stopMobileCameraScanner();

      await handleSearch(cleanBarcode);
      return;
    }

    setForm((prev) => ({
      ...prev,
      barcode: cleanBarcode,
    }));

    await stopMobileCameraScanner();

    setTimeout(() => {
      nameInputRef.current?.focus();
    }, 100);
  };

  const startMobileCameraScanner = async () => {
    try {
      setCameraScannerError("");
      setCameraScannerOpen(true);

      // Wait until scanner popup is rendered
      setTimeout(async () => {
        try {
          const scanner = new Html5Qrcode("mobile-barcode-reader");
          scannerRef.current = scanner;

          await scanner.start(
            {
              facingMode: "environment",
            },
            {
              fps: 10,
              qrbox: {
                width: 280,
                height: 140,
              },
              aspectRatio: 1.7,
            },
            async (decodedText) => {
              await handleScannedBarcode(decodedText);
            },
            () => {
              // Ignore continuous scanning errors
            },
          );
        } catch (error) {
          console.error("Camera scanner error:", error);

          setCameraScannerError(
            "Camera could not start. Please allow camera permission and try again.",
          );
        }
      }, 150);
    } catch (error) {
      console.error("Scanner start error:", error);

      setCameraScannerError(
        "Camera could not start. Please allow camera permission and try again.",
      );
    }
  };

  // Donwload Inventory Function

  const downloadInventoryCsv = () => {
    if (products.length === 0) {
      alert("There are no products to download.");
      return;
    }

    const exportProducts = products.map((product) => ({
      name: product.name || "",
      barcode: String(product.barcode || ""),
      price: Number(product.price || 0).toFixed(2),
      cost_price: getCostPrice(product).toFixed(2),
      stock: Number(product.stock || 0),
      category: product.category || "grocery",
      vat_code: product.vat_code || "B",
      container_size_ml:
        normalizeContainerSizeMl(product.container_size_ml) || "",
    }));

    const csv = Papa.unparse(exportProducts);

    const blob = new Blob(["\uFEFF", csv], {
      type: "text/csv;charset=utf-8;",
    });

    const downloadUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");

    const shopName =
      shopSettings?.shop_table_name || shopSettings?.shop_name || "inventory";

    const safeShopName = String(shopName)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    const today = new Date().toISOString().slice(0, 10);

    downloadLink.href = downloadUrl;
    downloadLink.download = `${
      safeShopName || "inventory"
    }-products-${today}.csv`;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    URL.revokeObjectURL(downloadUrl);
  };

  return (
    <div className="admin-container product-page">
      {successMessage && (
        <div className="product-success-message">✓ {successMessage}</div>
      )}

      <div className="admin-left product-add-panel">
        <div className="product-add-panel-header">
          <div>
            <h2>Add Product</h2>
            <p>Add a new product to your shop inventory.</p>
          </div>

          <div className="product-add-panel-icon" aria-hidden="true">
            +
          </div>
        </div>

        <form onSubmit={addProduct} className="product-form">
          <div className="product-form-field product-field-barcode">
            <label htmlFor="add-product-barcode">
              Barcode <span>*</span>
            </label>

            <div className="barcode-input-row">
              <input
                id="add-product-barcode"
                ref={barcodeInputRef}
                type="text"
                placeholder="Scan or enter barcode"
                value={form.barcode}
                autoFocus
                className={errors.barcode ? "input-error" : ""}
                onChange={(e) => updateField("barcode", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    nameInputRef.current?.focus();
                  }
                }}
              />

              <button
                type="button"
                className="barcode-camera-btn"
                aria-label="Scan barcode with camera"
                onClick={() => {
                  scannerTargetRef.current = "add";
                  startMobileCameraScanner();
                }}
              >
                <span className="camera-icon">📷</span>
              </button>
            </div>

            {errors.barcode && (
              <div className="field-error">{errors.barcode}</div>
            )}
          </div>

          <div className="product-form-field product-field-name">
            <label htmlFor="add-product-name">
              Product Name <span>*</span>
            </label>

            <input
              id="add-product-name"
              ref={nameInputRef}
              className={`product-name-input ${
                errors.name ? "input-error" : ""
              }`}
              type="text"
              placeholder="Enter product name"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
            />

            {errors.name && <div className="field-error">{errors.name}</div>}
          </div>

          <div className="product-form-field product-field-price">
            <label htmlFor="add-product-price">
              Selling Price (€) <span>*</span>
            </label>

            <input
              id="add-product-price"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={form.price}
              className={errors.price ? "input-error" : ""}
              onChange={(e) => updateField("price", e.target.value)}
            />

            {errors.price && <div className="field-error">{errors.price}</div>}
          </div>

          <div className="product-form-field product-field-cost">
            <label htmlFor="add-product-cost">Cost Price (€)</label>

            <input
              id="add-product-cost"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={form.cost_price}
              className={errors.cost_price ? "input-error" : ""}
              onChange={(e) => updateField("cost_price", e.target.value)}
            />

            {errors.cost_price && (
              <div className="field-error">{errors.cost_price}</div>
            )}
          </div>

          <div className="product-form-field product-field-stock">
            <label htmlFor="add-product-stock">
              Opening Stock <span>*</span>
            </label>

            <input
              id="add-product-stock"
              type="number"
              step="1"
              min="0"
              placeholder="0"
              value={form.stock}
              className={errors.stock ? "input-error" : ""}
              onChange={(e) => updateField("stock", e.target.value)}
            />

            {errors.stock && <div className="field-error">{errors.stock}</div>}
          </div>

          <div className="product-form-field product-field-category">
            <label htmlFor="add-product-category">
              Category <span>*</span>
            </label>

            <select
  id="add-product-category"
  value={form.category}
  className={errors.category ? "input-error" : ""}
  onChange={(e) => updateField("category", e.target.value)}
  disabled={!shopSettingsLoaded}
>
              <option value="">
  {shopSettingsLoaded
    ? "Select Category"
    : "Loading categories..."}
</option>

              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.toUpperCase()}
                </option>
              ))}
            </select>

            {errors.category && (
              <div className="field-error">{errors.category}</div>
            )}
          </div>

          <div className="product-form-field product-field-vat">
            <label htmlFor="add-product-vat">
              VAT Rate <span>*</span>
            </label>

            <select
              id="add-product-vat"
              value={form.vat_code}
              className={errors.vat_code ? "input-error" : ""}
              onChange={(e) => updateField("vat_code", e.target.value)}
            >
              <option value="">Select VAT code</option>
              <option value="A">A (0%)</option>
              <option value="B">B (23%)</option>
              <option value="C">C (13.5%)</option>
              <option value="D">D (5%)</option>
              <option value="E">E (9%)</option>
            </select>

            {errors.vat_code && (
              <div className="field-error">{errors.vat_code}</div>
            )}
          </div>

          <div className="product-form-field product-field-container">
            <label htmlFor="add-product-container">Container Size</label>

            <div className="product-input-with-suffix">
              <input
                id="add-product-container"
                type="number"
                min="0"
                className="container-size-input"
                placeholder="Example: 500"
                value={form.container_size_ml}
                onChange={(e) =>
                  updateField("container_size_ml", e.target.value)
                }
              />

              <span>ml</span>
            </div>
          </div>

          <div className="product-form-field product-field-supplier">
            <label htmlFor="add-product-supplier">Supplier</label>

            <select
              id="add-product-supplier"
              value={form.supplier_id}
              onChange={(e) => updateField("supplier_id", e.target.value)}
              disabled={suppliersLoading}
            >
              <option value="">
                {suppliersLoading ? "Loading suppliers..." : "No Supplier"}
              </option>

              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div className="product-form-footer">
            <p>
              Fields marked with <span>*</span> are required.
            </p>

            <button
              type="submit"
              className="product-add-submit"
              disabled={isSaving}
            >
              <span className="product-submit-icon">＋</span>

              {isSaving ? "Adding Product..." : "Add Product"}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-center">
        <div className="csv-import-section">
          <div className="csv-import-header">
            <div>
              <h3>Import Products from CSV</h3>

              <p>
                Upload a Products CSV file. Existing products will be matched
                and updated using their barcode.
              </p>
            </div>

            <label className="csv-file-button">
              Choose CSV File
              <input
                ref={csvFileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleCsvFile}
                className="csv-file-input"
              />
            </label>
          </div>

          {importFileName && (
            <div className="csv-file-name">
              Selected file: <strong>{importFileName}</strong>
            </div>
          )}

          {importMessage && (
            <div className="csv-import-message">{importMessage}</div>
          )}

          {importProducts.length > 0 && (
            <>
              <div className="csv-summary">
                <span>
                  Valid products: <strong>{importProducts.length}</strong>
                </span>

                <span>
                  Errors: <strong>{importErrors.length}</strong>
                </span>
              </div>

              <div className="csv-preview-wrapper">
                <table className="csv-preview-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Barcode</th>
                      <th>Price</th>
                      <th>Stock</th>
                      <th>Category</th>
                      <th>VAT</th>
                      <th>Size</th>
                      <th>DRS</th>
                    </tr>
                  </thead>

                  <tbody>
                    {importProducts.slice(0, 20).map((product, index) => (
                      <tr key={`${product.barcode}-${index}`}>
                        <td>{product.name}</td>
                        <td>{product.barcode}</td>
                        <td>€{Number(product.price || 0).toFixed(2)}</td>
                        <td>{product.stock}</td>
                        <td>{product.category}</td>
                        <td>{product.vat_code}</td>
                        <td>
                          {product.container_size_ml
                            ? `${product.container_size_ml}ml`
                            : "-"}
                        </td>
                        <td>
                          {product.is_drs
                            ? `€${Number(product.drs_amount || 0).toFixed(2)}`
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {importProducts.length > 20 && (
                  <p className="csv-preview-note">
                    Showing the first 20 of {importProducts.length} products.
                  </p>
                )}
              </div>

              <div className="csv-import-actions">
                <button
                  type="button"
                  className="csv-import-button"
                  onClick={submitCsvImport}
                  disabled={isImporting}
                >
                  {isImporting
                    ? "Importing Products..."
                    : `Import ${importProducts.length} Products`}
                </button>

                <button
                  type="button"
                  className="csv-clear-button"
                  onClick={clearCsvImport}
                  disabled={isImporting}
                >
                  Clear CSV
                </button>
              </div>
            </>
          )}

          {importErrors.length > 0 && (
            <details className="csv-errors">
              <summary>View {importErrors.length} CSV errors</summary>

              <div className="csv-error-list">
                {importErrors.slice(0, 50).map((error, index) => (
                  <div key={index}>
                    {error.row ? `Row ${error.row}: ` : ""}
                    {error.barcode ? `Barcode ${error.barcode}: ` : ""}
                    {error.message}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <div className="mobile-search-heading">Search Existing Product</div>

        <div className="product-search-box">
          <div className="product-search-input-row">
            <input
              type="text"
              placeholder="Search product by name or barcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              className="product-search-input"
            />

            <button
              type="button"
              className="barcode-camera-btn search-camera-btn"
              onClick={() => {
                scannerTargetRef.current = "search";
                startMobileCameraScanner();
              }}
            >
              <span className="camera-icon">📷</span>
            </button>
          </div>

          <button onClick={() => handleSearch()} className="search-btn">
            Search
          </button>

          <button
            onClick={() => {
              setSearch("");
              fetchProducts();
            }}
            className="clear-btn"
          >
            Clear
          </button>
        </div>

        <div className="inventory-products-section">
          <h2>Inventory</h2>

          <div className="mobile-products-list">
            {products.length > 0 ? (
              products.map((p) => (
                <div className="mobile-product-card" key={p.id}>
                  <div className="mobile-product-card-title">
                    {p.name || "Unnamed Product"}
                  </div>

                  <div className="mobile-product-card-row">
                    <span>Barcode</span>
                    <strong>{p.barcode || "-"}</strong>
                  </div>

                  <div className="mobile-product-card-row">
                    <span>Price</span>
                    <strong>€{Number(p.price || 0).toFixed(2)}</strong>
                  </div>

                  <div className="mobile-product-card-row">
                    <span>Stock</span>
                    <strong>{p.stock ?? 0}</strong>
                  </div>

                  <div className="mobile-product-card-row">
                    <span>Category</span>
                    <strong>{p.category || "-"}</strong>
                  </div>

                  <div className="mobile-product-card-row">
                    <span>Supplier</span>
                    <strong>{p.supplier_name || "-"}</strong>
                  </div>

                  <div className="mobile-product-card-row">
                    <span>DRS</span>
                    <strong>{getDrsLabel(p)}</strong>
                  </div>

                  <div className="mobile-product-card-actions">
                    <button
                      type="button"
                      className="product-icon-btn product-edit-btn"
                      onClick={() => handleEdit(p)}
                      title="Edit product"
                    >
                      ✎
                    </button>

                    <button
                      type="button"
                      className="product-icon-btn product-delete-btn"
                      onClick={() => deleteProduct(p)}
                      title="Delete product"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="mobile-product-card">
                <div className="mobile-product-card-title">
                  No products found
                </div>
              </div>
            )}
          </div>

          <div className="inventory-products-scroll">
            <table className="product-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Barcode</th>
                  <th>Stock</th>
                  <th>Category</th>
                  <th>Supplier</th>
                  <th>DRS</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {products.length > 0 ? (
                  products.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>€{Number(p.price || 0).toFixed(2)}</td>
                      <td>{p.barcode}</td>
                      <td>{p.stock}</td>
                      <td>{p.category}</td>
                      <td>{p.supplier_name || "-"}</td>
                      <td>{getDrsLabel(p)}</td>

                      <td className="product-action-cell">
                        <button
                          type="button"
                          className="product-icon-btn product-edit-btn"
                          onClick={() => handleEdit(p)}
                          title="Edit product"
                        >
                          ✎
                        </button>

                        <button
                          type="button"
                          className="product-icon-btn product-delete-btn"
                          onClick={() => deleteProduct(p)}
                          title="Delete product"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8">No products found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="inventory-footer">
            <div className="inventory-product-count">
              Showing <strong>{products.length}</strong>{" "}
              {products.length === 1 ? "product" : "products"}
            </div>

            <button
              type="button"
              className="inventory-download-btn"
              onClick={downloadInventoryCsv}
              disabled={products.length === 0}
            >
              <span className="inventory-download-icon">↓</span>
              Download Inventory
            </button>
          </div>

          {editingProduct && (
            <div className="modal-overlay">
              <div className="modal-card">
                <h2 className="modal-title">Edit Product</h2>

                <div className="form-group">
                  <label>Name</label>
                  <input
                    value={editingProduct.name || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Selling Price (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.price || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        price: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Cost Price (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.cost_price || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        cost_price: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Stock</label>
                  <input
                    type="number"
                    value={editingProduct.stock || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        stock: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Barcode</label>
                  <input
                    value={editingProduct.barcode || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        barcode: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={editingProduct.category || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        category: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Category</option>

                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>VAT Code</label>
                  <select
                    value={editingProduct.vat_code || "B"}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        vat_code: e.target.value,
                      })
                    }
                  >
                    <option value="">Select VAT code</option>
                    <option value="A">A (0%)</option>
                    <option value="B">B (23%)</option>
                    <option value="C">C (13.5%)</option>
                    <option value="D">D (5%)</option>
                    <option value="E">E (9%)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Container Size ML</label>
                  <input
                    type="number"
                    placeholder="Container Size ML e.g. 500"
                    value={editingProduct.container_size_ml || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        container_size_ml: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Supplier</label>

                  <select
                    value={editingProduct.supplier_id || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        supplier_id: e.target.value,
                      })
                    }
                    disabled={suppliersLoading}
                  >
                    <option value="">
                      {suppliersLoading
                        ? "Loading suppliers..."
                        : "No Supplier"}
                    </option>

                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>DRS Preview</label>
                  <input
                    readOnly
                    value={(() => {
                      const size =
                        normalizeContainerSizeMl(
                          editingProduct.container_size_ml,
                        ) || extractContainerSizeFromText(editingProduct.name);

                      const drs = getDrsDetailsFromSize(size);

                      if (!drs.is_drs) {
                        return "No DRS";
                      }

                      return `${size}ml / €${drs.drs_amount.toFixed(2)}`;
                    })()}
                  />
                </div>

                <div className="modal-actions">
                  <button className="save-btn" onClick={saveProduct}>
                    Save
                  </button>

                  <button
                    className="cancel-btn"
                    onClick={() => setEditingProduct(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {cameraScannerOpen && (
        <div className="camera-scanner-overlay">
          <div className="camera-scanner-modal">
            <div className="camera-scanner-header">
              <div>
                <h3>Scan Barcode</h3>
                <p>Point the camera at the product barcode.</p>
              </div>

              <button
                type="button"
                className="camera-scanner-close"
                onClick={stopMobileCameraScanner}
              >
                ×
              </button>
            </div>

            <div id="mobile-barcode-reader" />

            {cameraScannerError && (
              <div className="camera-scanner-error">{cameraScannerError}</div>
            )}

            <button
              type="button"
              className="camera-scanner-cancel"
              onClick={stopMobileCameraScanner}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductPage;
